import React, { useLayoutEffect, useRef, useState } from "react";
import { Table, Spinner, Alert } from "@themesberg/react-bootstrap";
import { checkStrEmpty } from "../redux/utils";
import { useResponsive } from "../hooks/useResponsive";
import TableScroller from "./TableScroller";

function CustomTable(props) {
  const {
    fields = [],
    data = [],
    isLoading = false,
    className = "",
    tableHeadStyle = {},
    headBackground = "#099019",
    headColor = "white",
    getRowStyle = f=>f,
    error = null,
  } = props;

  const { isMobile } = useResponsive();
  const scrollerRef = useRef(null);

  // Which column is the row action, and where does it sit? An Action column at
  // either edge can be pinned there; one buried in the middle cannot.
  const visibleFields = fields
    .map((field, index) => ({ field, index }))
    .filter(({ field }) => !field.hidden);
  const actionIndex = visibleFields.findIndex(({ field }) => {
    const t = field.title;
    // title may be a React element or non-string — only test plain strings
    return typeof t === "string" && /^actions?$/i.test(t.trim());
  });
  const candidateSide =
    actionIndex === -1
      ? null
      : actionIndex === 0
      ? "left"
      : actionIndex === visibleFields.length - 1
      ? "right"
      : null;

  // Pinning a column that is itself most of the screen helps nobody, so only pin
  // when the action cell leaves the majority of the width for the data.
  const [stickySide, setStickySide] = useState(null);

  useLayoutEffect(() => {
    if (!candidateSide) {
      setStickySide(null);
      return undefined;
    }

    const measure = () => {
      const scroller = scrollerRef.current;
      const th = scroller && scroller.querySelector("thead th.is-sticky-col");
      if (!th || !scroller || !scroller.clientWidth) return;
      // A pinned column takes its width away from the data, so only pin a
      // genuinely compact one (icon buttons / a single small button): at most 40%
      // of the scroller and never more than ~110px.
      const width = th.getBoundingClientRect().width;
      const fits = width <= Math.min(scroller.clientWidth * 0.4, 110);
      setStickySide(fits ? candidateSide : null);
    };

    // The first layout pass can run before the table has its final width, so
    // measure again just after mount as well as on any resize.
    measure();
    const timers = [setTimeout(measure, 0), setTimeout(measure, 300)];
    let observer;
    const scroller = scrollerRef.current;
    if (scroller && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(scroller);
    }
    window.addEventListener("resize", measure);

    return () => {
      timers.forEach(clearTimeout);
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [candidateSide, data.length, fields.length, isMobile]);

  const stickyClasses = (fieldIndex) => {
    if (fieldIndex !== (visibleFields[actionIndex] && visibleFields[actionIndex].index)) {
      return "";
    }
    return `is-sticky-col${stickySide ? ` is-sticky-${stickySide}` : ""}`;
  };

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  return (
    <>
      {/* No swipe hint when there are no rows: scrolling a header only is not useful. */}
      <TableScroller scrollerRef={scrollerRef} hint={data.length > 0}>
        <Table
          hover
          className={`user-table align-items-center ${className}`}
          style={{
            width: "100%",
            fontSize: isMobile ? '0.8rem' : '0.875rem'
          }}
          {...props}
          bordered
        >
          <thead className="table-header">
            <tr className="file-list-item">
              {fields.map((_item, _idx) => {
                 if (_item.hidden) {
                  return null;
                } else return (
                <th
                  key={_idx}
                  className={stickyClasses(_idx)}
                  style={{
                    ...tableHeadStyle,
                    backgroundColor: headBackground,
                    color: headColor,
                    textAlign: "center",
                    fontSize: isMobile ? '0.75rem' : '0.875rem',
                    padding: isMobile ? '0.5rem 0.25rem' : '0.75rem',
                    whiteSpace: isMobile ? 'nowrap' : 'normal'
                  }}
                >
                  {_item.headerComponent ? _item.headerComponent()
                : _item.title}
                </th>
              )})}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={fields.length} className="text-center py-4">
                  <Spinner animation="border" variant="primary" />
                  {!isMobile && <p className="mt-2 mb-0 text-muted">Loading...</p>}
                </td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <tr key={idx} style={getRowStyle(item)}>
                  {fields.map((_item, _idx) => {
                    let val = item[_item.value] || "";
                    let value_alt =
                      (_item.value_alt && item[_item.value_alt]) || "";
                    if (_item.component) {
                      return (
                        <td
                          key={_idx}
                          className={`${_item.className || ""} ${stickyClasses(_idx)}`.trim()}
                          style={{
                            ...(typeof _item.style === "function"
                              ? _item.style(item)
                              : _item.style),
                            padding: isMobile ? '0.5rem 0.25rem' : '0.75rem',
                            fontSize: isMobile ? '0.75rem' : '0.875rem'
                          }}
                        >
                          {_item.component(item, idx)}
                        </td>
                      );
                    } else if (_item.hidden) {
                      return null;
                    } else {
                      return (
                        <td
                          key={_idx}
                          className={`${_item.className || ""} ${stickyClasses(_idx)}`.trim()}
                          style={{
                            ...(typeof _item.style === "function"
                              ? _item.style(item)
                              : _item.style),
                            padding: isMobile ? '0.5rem 0.25rem' : '0.75rem',
                            fontSize: isMobile ? '0.75rem' : '0.875rem',
                            whiteSpace: isMobile ? 'nowrap' : 'normal'
                          }}
                        >
                          {checkStrEmpty(val.toString())
                            ? value_alt
                            : val.toString()}
                        </td>
                      );
                    }
                  })}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableScroller>
      {data.length === 0 && !isLoading && (
        <div className="table-empty-state" role="status">
          <i className="fas fa-info-circle me-2" aria-hidden="true"></i>
          No records found.
        </div>
      )}
    </>
  );
}

export default CustomTable;
